import { useState, useEffect } from "react";
import { Plus, Trash2 } from "lucide-react";

import {
  Field,
  TextInput,
  TextArea,
  CollapsibleSection,
  CTAGroup,
  ReorderButtons,
  SelectInput,
} from "../../../components/FormField";

import { MediaImageField } from "../../../components/MediaPickerDialog";
import { emptyButton, moveItem } from "./shared";
import { ICON_OPTIONS } from "../../../lib/iconsData";

export function AboutSections({ setSectionsData, initialData }) {
  /* ---------------- HERO ---------------- */
  const [heroActive, setHeroActive] = useState(true);
  const [heroBadgeText, setHeroBadgeText] = useState("About ThynkProcess");
  const [heroHeading, setHeroHeading] = useState(
    "We Are ThynkProcess — Your Partner in",
  );
  const [heroHeadingEmphasis, setHeroHeadingEmphasis] = useState(
    "Digital Transformation",
  );
  const [heroDescription, setHeroDescription] = useState("");
  const [heroVideos, setHeroVideos] = useState([
    "/videos/hero-video-1.mp4",
    "/videos/hero-video-2.mp4",
    "/videos/hero-video-3.mp4",
  ]);
  const [heroPrimary, setHeroPrimary] = useState(emptyButton());
  const [heroSecondary, setHeroSecondary] = useState(emptyButton());
  const [trustBadges, setTrustBadges] = useState([
    { icon: "Award", title: "SAP Partner", sub: "Certified Experts" },
    { icon: "Users", title: "250+ Companies", sub: "Served Globally" },
    { icon: "Shield", title: "25+ Years", sub: "Industry Experience" },
  ]);

  /* ---------------- OUR STORY ---------------- */
  const [storyActive, setStoryActive] = useState(true);
  const [storyBadgeText, setStoryBadgeText] = useState("Our Story");
  const [storyHeading, setStoryHeading] = useState("Our Story");
  const [storyParagraphs, setStoryParagraphs] = useState(["", "", ""]);
  const [storyImage, setStoryImage] = useState("");
  const [storyImageAlt, setStoryImageAlt] = useState(
    "ThynkProcess Melbourne office team collaboration",
  );

  /* ---------------- STATS ---------------- */
  const [statsActive, setStatsActive] = useState(true);
  const [statsHeading, setStatsHeading] = useState(
    "ThynkProcess by the Numbers",
  );
  const [statsItems, setStatsItems] = useState([
    { value: "150+", label: "Successful ERP & CRM Projects" },
    { value: "60+", label: "Years Combined ERP Experience" },
    { value: "10+", label: "Industries Served" },
    { value: "SAP", label: "Silver Partner — Certified" },
  ]);

  /* ---------------- VALUES ---------------- */
  const [valuesActive, setValuesActive] = useState(true);
  const [valuesBadgeText, setValuesBadgeText] = useState("Our Values");
  const [valuesHeading, setValuesHeading] = useState("What We Stand For");
  const [valuesDescription, setValuesDescription] = useState("");
  const [valuesCards, setValuesCards] = useState([
    { title: "", description: "", icon: "" },
  ]);

  /* ---------------- METHODOLOGY ---------------- */
  const [methActive, setMethActive] = useState(true);
  const [methBadgeText, setMethBadgeText] = useState("Our Approach");
  const [methHeading, setMethHeading] = useState("How We Work");
  const [methDescription, setMethDescription] = useState("");
  const [methCTAText, setMethCTAText] = useState(
    "Read More About Our Methodology",
  );
  const [methCTALink, setMethCTALink] = useState("/about/approach");
  const [methSteps, setMethSteps] = useState([
    { step: 1, title: "", description: "" },
  ]);

  /* ---------------- PARTNERS ---------------- */
  const [partnersActive, setPartnersActive] = useState(true);
  const [partnersBadgeText, setPartnersBadgeText] = useState(
    "Technology Partners",
  );
  const [partnersHeading, setPartnersHeading] = useState(
    "Our Technology Partners",
  );
  const [partnersDescription, setPartnersDescription] = useState("");
  const [partnersCTAText, setPartnersCTAText] = useState("View All Partners");
  const [partnersCTALink, setPartnersCTALink] = useState("/about/partners");
  const [partnersItems, setPartnersItems] = useState(["SAP", "Salesforce"]);

  /* ---------------- CASE STUDIES ---------------- */
  const [csActive, setCsActive] = useState(true);
  const [csBadgeText, setCsBadgeText] = useState("Results We've Delivered");
  const [csHeading, setCsHeading] = useState("Results We've Delivered");
  const [csCTAText, setCsCTAText] = useState("View All Case Studies");
  const [csCTALink, setCsCTALink] = useState("/resources/case-studies");
  const [csItems, setCsItems] = useState([
    { quote: "", author: "", company: "" },
  ]);

  /* ---------------- OUR TEAM ---------------- */
  const [teamActive, setTeamActive] = useState(true);
  const [teamBadgeText, setTeamBadgeText] = useState("Our Team");
  const [teamHeading, setTeamHeading] = useState(
    "Meet the Experts Behind ThynkProcess",
  );
  const [teamDescription, setTeamDescription] = useState("");
  const [teamBullets, setTeamBullets] = useState([{ icon: "Zap", text: "" }]);
  const [teamExploreHeading, setTeamExploreHeading] = useState("Explore More");
  const [teamExploreLinks, setTeamExploreLinks] = useState([
    { label: "", href: "" },
  ]);

  /* ---------------- FINAL CTA ---------------- */
  const [ctaActive, setCtaActive] = useState(true);
  const [ctaHeading, setCtaHeading] = useState(
    "Let's Talk About Your Business",
  );
  const [ctaDescription, setCtaDescription] = useState("");
  const [ctaPrimary, setCtaPrimary] = useState(emptyButton());
  const [ctaSecondary, setCtaSecondary] = useState(emptyButton());
  const [ctaContactLine, setCtaContactLine] = useState(
    "+61 3 7075 3900 | info@thynkprocess.com.au | South Melbourne, Victoria",
  );

  const updateBtn = (setter, key, val) => setter((b) => ({ ...b, [key]: val }));

  /* ---------------- POPULATE FROM API ---------------- */
  useEffect(() => {
    if (!initialData) return;
    const d = initialData;

    if (d.hero) {
      setHeroBadgeText(d.hero.badge_text || "");
      setHeroHeading(d.hero.heading || "");
      setHeroHeadingEmphasis(d.hero.heading_emphasis || "");
      setHeroDescription(d.hero.description || "");
      if (d.hero.videos?.length) setHeroVideos(d.hero.videos);
      if (d.hero.primary_cta)
        setHeroPrimary({
          text: d.hero.primary_cta.text || "",
          url: d.hero.primary_cta.link || "",
          newTab: false,
        });
      if (d.hero.secondary_cta)
        setHeroSecondary({
          text: d.hero.secondary_cta.text || "",
          url: d.hero.secondary_cta.link || "",
          newTab: false,
        });
      if (d.hero.trust_badges?.length) setTrustBadges(d.hero.trust_badges);
    }

    if (d.our_story) {
      setStoryBadgeText(d.our_story.badge_text || "");
      setStoryHeading(d.our_story.heading || "");
      if (d.our_story.paragraphs?.length)
        setStoryParagraphs(d.our_story.paragraphs);
      setStoryImage(d.our_story.image || "");
      setStoryImageAlt(d.our_story.image_alt || "");
    }

    if (d.stats) {
      setStatsHeading(d.stats.heading || "");
      if (d.stats.items?.length) setStatsItems(d.stats.items);
    }

    if (d.values) {
      setValuesBadgeText(d.values.badge_text || "");
      setValuesHeading(d.values.heading || "");
      setValuesDescription(d.values.description || "");
      if (d.values.cards?.length) setValuesCards(d.values.cards);
    }

    if (d.methodology) {
      setMethBadgeText(d.methodology.badge_text || "");
      setMethHeading(d.methodology.heading || "");
      setMethDescription(d.methodology.description || "");
      setMethCTAText(d.methodology.cta_text || "");
      setMethCTALink(d.methodology.cta_link || "");
      if (d.methodology.steps?.length) setMethSteps(d.methodology.steps);
    }

    if (d.partners) {
      setPartnersBadgeText(d.partners.badge_text || "");
      setPartnersHeading(d.partners.heading || "");
      setPartnersDescription(d.partners.description || "");
      setPartnersCTAText(d.partners.cta_text || "");
      setPartnersCTALink(d.partners.cta_link || "");
      if (d.partners.items?.length) setPartnersItems(d.partners.items);
    }

    if (d.case_studies) {
      setCsBadgeText(d.case_studies.badge_text || "");
      setCsHeading(d.case_studies.heading || "");
      setCsCTAText(d.case_studies.cta_text || "");
      setCsCTALink(d.case_studies.cta_link || "");
      if (d.case_studies.items?.length) setCsItems(d.case_studies.items);
    }

    if (d.our_team) {
      setTeamBadgeText(d.our_team.badge_text || "");
      setTeamHeading(d.our_team.heading || "");
      setTeamDescription(d.our_team.description || "");
      if (d.our_team.bullets?.length) setTeamBullets(d.our_team.bullets);
      setTeamExploreHeading(d.our_team.explore_heading || "");
      if (d.our_team.explore_links?.length)
        setTeamExploreLinks(d.our_team.explore_links);
    }

    if (d.final_cta) {
      setCtaHeading(d.final_cta.heading || "");
      setCtaDescription(d.final_cta.description || "");
      if (d.final_cta.primary_cta)
        setCtaPrimary({
          text: d.final_cta.primary_cta.text || "",
          url: d.final_cta.primary_cta.link || "",
          newTab: false,
        });
      if (d.final_cta.secondary_cta)
        setCtaSecondary({
          text: d.final_cta.secondary_cta.text || "",
          url: d.final_cta.secondary_cta.link || "",
          newTab: false,
        });
      setCtaContactLine(d.final_cta.contact_line || "");
    }
  }, [initialData]);

  const buildPayload = () => ({
    hero: {
      badge_text: heroBadgeText,
      heading: heroHeading,
      heading_emphasis: heroHeadingEmphasis,
      description: heroDescription,
      videos: heroVideos,
      primary_cta: { text: heroPrimary.text, link: heroPrimary.url },
      secondary_cta: { text: heroSecondary.text, link: heroSecondary.url },
      trust_badges: trustBadges,
    },
    our_story: {
      badge_text: storyBadgeText,
      heading: storyHeading,
      paragraphs: storyParagraphs,
      image: storyImage,
      image_alt: storyImageAlt,
    },
    stats: {
      heading: statsHeading,
      items: statsItems,
    },
    values: {
      badge_text: valuesBadgeText,
      heading: valuesHeading,
      description: valuesDescription,
      cards: valuesCards,
    },
    methodology: {
      badge_text: methBadgeText,
      heading: methHeading,
      description: methDescription,
      cta_text: methCTAText,
      cta_link: methCTALink,
      steps: methSteps,
    },
    partners: {
      badge_text: partnersBadgeText,
      heading: partnersHeading,
      description: partnersDescription,
      cta_text: partnersCTAText,
      cta_link: partnersCTALink,
      items: partnersItems,
    },
    case_studies: {
      badge_text: csBadgeText,
      heading: csHeading,
      cta_text: csCTAText,
      cta_link: csCTALink,
      items: csItems,
    },
    our_team: {
      badge_text: teamBadgeText,
      heading: teamHeading,
      description: teamDescription,
      bullets: teamBullets,
      explore_heading: teamExploreHeading,
      explore_links: teamExploreLinks,
    },
    final_cta: {
      heading: ctaHeading,
      description: ctaDescription,
      primary_cta: { text: ctaPrimary.text, link: ctaPrimary.url },
      secondary_cta: { text: ctaSecondary.text, link: ctaSecondary.url },
      contact_line: ctaContactLine,
    },
  });

  useEffect(() => {
    setSectionsData?.(buildPayload());
  }, [
    heroBadgeText,
    heroHeading,
    heroHeadingEmphasis,
    heroDescription,
    heroVideos,
    heroPrimary,
    heroSecondary,
    trustBadges,
    storyBadgeText,
    storyHeading,
    storyParagraphs,
    storyImage,
    storyImageAlt,
    statsHeading,
    statsItems,
    valuesBadgeText,
    valuesHeading,
    valuesDescription,
    valuesCards,
    methBadgeText,
    methHeading,
    methDescription,
    methCTAText,
    methCTALink,
    methSteps,
    partnersBadgeText,
    partnersHeading,
    partnersDescription,
    partnersCTAText,
    partnersCTALink,
    partnersItems,
    csBadgeText,
    csHeading,
    csCTAText,
    csCTALink,
    csItems,
    teamBadgeText,
    teamHeading,
    teamDescription,
    teamBullets,
    teamExploreHeading,
    teamExploreLinks,
    ctaHeading,
    ctaDescription,
    ctaPrimary,
    ctaSecondary,
    ctaContactLine,
  ]);

  return (
    <>
      {/* HERO */}
      <CollapsibleSection
        title="Hero Section"
        active={heroActive}
        onToggleActive={setHeroActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={heroBadgeText}
            onChange={(e) => setHeroBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={heroHeading}
            onChange={(e) => setHeroHeading(e.target.value)}
          />
        </Field>
        <Field label="Heading Emphasis">
          <TextInput
            value={heroHeadingEmphasis}
            onChange={(e) => setHeroHeadingEmphasis(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={heroDescription}
            onChange={(e) => setHeroDescription(e.target.value)}
          />
        </Field>

        {/* Hero Videos */}
        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Hero Videos</p>
          <button
            onClick={() => setHeroVideos((s) => [...s, ""])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Video
          </button>
        </div>
        {heroVideos.map((v, i) => (
          <div key={i} className="flex gap-2 items-center mb-2">
            <div className="flex-1">
              <MediaImageField
                label={`Video ${i + 1}`}
                value={v}
                onChange={(val) =>
                  setHeroVideos((s) => s.map((x, idx) => (idx === i ? val : x)))
                }
              />
            </div>
            {heroVideos.length > 1 && (
              <button
                onClick={() =>
                  setHeroVideos((s) => s.filter((_, idx) => idx !== i))
                }
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}

        <CTAGroup
          label="Primary CTA"
          text={heroPrimary.text}
          url={heroPrimary.url}
          newTab={heroPrimary.newTab}
          onTextChange={(v) => updateBtn(setHeroPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={heroSecondary.text}
          url={heroSecondary.url}
          newTab={heroSecondary.newTab}
          onTextChange={(v) => updateBtn(setHeroSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setHeroSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setHeroSecondary, "newTab", v)}
        />

        <div className="flex justify-between mt-6 mb-2">
          <p className="text-xs font-semibold">Trust Badges</p>
          <button
            onClick={() =>
              setTrustBadges((s) => [...s, { icon: "", title: "", sub: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Badge
          </button>
        </div>
        {trustBadges.map((b, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Badge {i + 1}</p>
              {trustBadges.length > 1 && (
                <button
                  onClick={() =>
                    setTrustBadges((s) => s.filter((_, idx) => idx !== i))
                  }
                >
                  <Trash2 size={14} />
                </button>
              )}
            </div>
            <Field label="Icon">
              <SelectInput
                value={b.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setTrustBadges((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Title">
                <TextInput
                  value={b.title}
                  onChange={(e) =>
                    setTrustBadges((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, title: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Sub">
                <TextInput
                  value={b.sub}
                  onChange={(e) =>
                    setTrustBadges((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, sub: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* OUR STORY */}
      <CollapsibleSection
        title="Our Story"
        active={storyActive}
        onToggleActive={setStoryActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={storyBadgeText}
            onChange={(e) => setStoryBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={storyHeading}
            onChange={(e) => setStoryHeading(e.target.value)}
          />
        </Field>
        <Field label="Section Image">
          <MediaImageField
            label="Select Image"
            value={storyImage}
            onChange={setStoryImage}
          />
        </Field>
        <Field label="Image Alt Text">
          <TextInput
            value={storyImageAlt}
            onChange={(e) => setStoryImageAlt(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Paragraphs</p>
          <button
            onClick={() => setStoryParagraphs((s) => [...s, ""])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Paragraph
          </button>
        </div>
        {storyParagraphs.map((p, i) => (
          <div key={i} className="flex gap-2 items-start mb-2">
            <div className="flex-1">
              <Field label={`Paragraph ${i + 1}`}>
                <TextArea
                  value={p}
                  onChange={(e) =>
                    setStoryParagraphs((s) =>
                      s.map((x, idx) => (idx === i ? e.target.value : x)),
                    )
                  }
                />
              </Field>
            </div>
            {storyParagraphs.length > 1 && (
              <button
                className="mt-6"
                onClick={() =>
                  setStoryParagraphs((s) => s.filter((_, idx) => idx !== i))
                }
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </CollapsibleSection>

      {/* STATS */}
      <CollapsibleSection
        title="Stats"
        active={statsActive}
        onToggleActive={setStatsActive}
      >
        <Field label="Heading">
          <TextInput
            value={statsHeading}
            onChange={(e) => setStatsHeading(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Stat Items</p>
          <button
            onClick={() =>
              setStatsItems((s) => [...s, { value: "", label: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Stat
          </button>
        </div>
        {statsItems.map((s, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3">
            <div className="flex justify-between mb-2">
              <p className="text-xs font-semibold">Stat {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={statsItems.length}
                  onMove={(f, t) => setStatsItems(moveItem(statsItems, f, t))}
                />
                {statsItems.length > 1 && (
                  <button
                    onClick={() =>
                      setStatsItems((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Value">
                <TextInput
                  value={s.value}
                  onChange={(e) =>
                    setStatsItems((st) =>
                      st.map((x, idx) =>
                        idx === i ? { ...x, value: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Label">
                <TextInput
                  value={s.label}
                  onChange={(e) =>
                    setStatsItems((st) =>
                      st.map((x, idx) =>
                        idx === i ? { ...x, label: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* VALUES */}
      <CollapsibleSection
        title="Values"
        active={valuesActive}
        onToggleActive={setValuesActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={valuesBadgeText}
            onChange={(e) => setValuesBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={valuesHeading}
            onChange={(e) => setValuesHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={valuesDescription}
            onChange={(e) => setValuesDescription(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Value Cards</p>
          <button
            onClick={() =>
              setValuesCards((s) => [
                ...s,
                { title: "", description: "", icon: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Value
          </button>
        </div>
        {valuesCards.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Value {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={valuesCards.length}
                  onMove={(f, t) => setValuesCards(moveItem(valuesCards, f, t))}
                />
                {valuesCards.length > 1 && (
                  <button
                    onClick={() =>
                      setValuesCards((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={c.title}
                onChange={(e) =>
                  setValuesCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={c.description}
                onChange={(e) =>
                  setValuesCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Icon">
              <SelectInput
                value={c.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setValuesCards((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* METHODOLOGY */}
      <CollapsibleSection
        title="How We Work (Methodology)"
        active={methActive}
        onToggleActive={setMethActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={methBadgeText}
            onChange={(e) => setMethBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={methHeading}
            onChange={(e) => setMethHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={methDescription}
            onChange={(e) => setMethDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={methCTAText}
              onChange={(e) => setMethCTAText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={methCTALink}
              onChange={(e) => setMethCTALink(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Steps</p>
          <button
            onClick={() =>
              setMethSteps((s) => [
                ...s,
                { step: s.length + 1, title: "", description: "" },
              ])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Step
          </button>
        </div>
        {methSteps.map((s, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Step {s.step}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={methSteps.length}
                  onMove={(f, t) => setMethSteps(moveItem(methSteps, f, t))}
                />
                {methSteps.length > 1 && (
                  <button
                    onClick={() =>
                      setMethSteps((st) =>
                        st
                          .filter((_, idx) => idx !== i)
                          .map((x, idx) => ({ ...x, step: idx + 1 })),
                      )
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Title">
              <TextInput
                value={s.title}
                onChange={(e) =>
                  setMethSteps((st) =>
                    st.map((x, idx) =>
                      idx === i ? { ...x, title: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Description">
              <TextArea
                value={s.description}
                onChange={(e) =>
                  setMethSteps((st) =>
                    st.map((x, idx) =>
                      idx === i ? { ...x, description: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}
      </CollapsibleSection>

      {/* PARTNERS */}
      <CollapsibleSection
        title="Technology Partners"
        active={partnersActive}
        onToggleActive={setPartnersActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={partnersBadgeText}
            onChange={(e) => setPartnersBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={partnersHeading}
            onChange={(e) => setPartnersHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={partnersDescription}
            onChange={(e) => setPartnersDescription(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={partnersCTAText}
              onChange={(e) => setPartnersCTAText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={partnersCTALink}
              onChange={(e) => setPartnersCTALink(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Partner Names</p>
          <button
            onClick={() => setPartnersItems((s) => [...s, ""])}
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Partner
          </button>
        </div>
        {partnersItems.map((p, i) => (
          <div key={i} className="flex gap-2 items-center mb-2">
            <div className="flex-1">
              <TextInput
                value={p}
                placeholder="e.g. SAP"
                onChange={(e) =>
                  setPartnersItems((s) =>
                    s.map((x, idx) => (idx === i ? e.target.value : x)),
                  )
                }
              />
            </div>
            {partnersItems.length > 1 && (
              <button
                onClick={() =>
                  setPartnersItems((s) => s.filter((_, idx) => idx !== i))
                }
              >
                <Trash2 size={14} />
              </button>
            )}
          </div>
        ))}
      </CollapsibleSection>

      {/* CASE STUDIES */}
      <CollapsibleSection
        title="Case Studies Carousel"
        active={csActive}
        onToggleActive={setCsActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={csBadgeText}
            onChange={(e) => setCsBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={csHeading}
            onChange={(e) => setCsHeading(e.target.value)}
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="CTA Text">
            <TextInput
              value={csCTAText}
              onChange={(e) => setCsCTAText(e.target.value)}
            />
          </Field>
          <Field label="CTA Link">
            <TextInput
              value={csCTALink}
              onChange={(e) => setCsCTALink(e.target.value)}
            />
          </Field>
        </div>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Case Studies</p>
          <button
            onClick={() =>
              setCsItems((s) => [...s, { quote: "", author: "", company: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Case Study
          </button>
        </div>
        {csItems.map((c, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Case Study {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={csItems.length}
                  onMove={(f, t) => setCsItems(moveItem(csItems, f, t))}
                />
                {csItems.length > 1 && (
                  <button
                    onClick={() =>
                      setCsItems((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Quote">
              <TextArea
                value={c.quote}
                onChange={(e) =>
                  setCsItems((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, quote: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Author">
                <TextInput
                  value={c.author}
                  onChange={(e) =>
                    setCsItems((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, author: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Company">
                <TextInput
                  value={c.company}
                  onChange={(e) =>
                    setCsItems((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, company: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* OUR TEAM */}
      <CollapsibleSection
        title="Our Team"
        active={teamActive}
        onToggleActive={setTeamActive}
      >
        <Field label="Badge Text">
          <TextInput
            value={teamBadgeText}
            onChange={(e) => setTeamBadgeText(e.target.value)}
          />
        </Field>
        <Field label="Heading">
          <TextInput
            value={teamHeading}
            onChange={(e) => setTeamHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={teamDescription}
            onChange={(e) => setTeamDescription(e.target.value)}
          />
        </Field>

        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Bullet Points</p>
          <button
            onClick={() =>
              setTeamBullets((s) => [...s, { icon: "", text: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Bullet
          </button>
        </div>
        {teamBullets.map((b, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3 space-y-3">
            <div className="flex justify-between">
              <p className="text-xs font-semibold">Bullet {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={teamBullets.length}
                  onMove={(f, t) => setTeamBullets(moveItem(teamBullets, f, t))}
                />
                {teamBullets.length > 1 && (
                  <button
                    onClick={() =>
                      setTeamBullets((s) => s.filter((_, idx) => idx !== i))
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <Field label="Icon">
              <SelectInput
                value={b.icon}
                options={ICON_OPTIONS}
                onChange={(e) =>
                  setTeamBullets((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, icon: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
            <Field label="Text">
              <TextInput
                value={b.text}
                onChange={(e) =>
                  setTeamBullets((s) =>
                    s.map((x, idx) =>
                      idx === i ? { ...x, text: e.target.value } : x,
                    ),
                  )
                }
              />
            </Field>
          </div>
        ))}

        <Field label="Explore Box Heading">
          <TextInput
            value={teamExploreHeading}
            onChange={(e) => setTeamExploreHeading(e.target.value)}
          />
        </Field>
        <div className="flex justify-between mt-4 mb-2">
          <p className="text-xs font-semibold">Explore Links</p>
          <button
            onClick={() =>
              setTeamExploreLinks((s) => [...s, { label: "", href: "" }])
            }
            className="flex items-center gap-1 text-xs"
          >
            <Plus size={14} /> Add Link
          </button>
        </div>
        {teamExploreLinks.map((l, i) => (
          <div key={i} className="border p-3 rounded-lg mb-3">
            <div className="flex justify-between mb-2">
              <p className="text-xs font-semibold">Link {i + 1}</p>
              <div className="flex gap-2">
                <ReorderButtons
                  index={i}
                  total={teamExploreLinks.length}
                  onMove={(f, t) =>
                    setTeamExploreLinks(moveItem(teamExploreLinks, f, t))
                  }
                />
                {teamExploreLinks.length > 1 && (
                  <button
                    onClick={() =>
                      setTeamExploreLinks((s) =>
                        s.filter((_, idx) => idx !== i),
                      )
                    }
                  >
                    <Trash2 size={14} />
                  </button>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Label">
                <TextInput
                  value={l.label}
                  onChange={(e) =>
                    setTeamExploreLinks((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, label: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
              <Field label="Link (href)">
                <TextInput
                  value={l.href}
                  onChange={(e) =>
                    setTeamExploreLinks((s) =>
                      s.map((x, idx) =>
                        idx === i ? { ...x, href: e.target.value } : x,
                      ),
                    )
                  }
                />
              </Field>
            </div>
          </div>
        ))}
      </CollapsibleSection>

      {/* FINAL CTA */}
      <CollapsibleSection
        title="Final CTA"
        active={ctaActive}
        onToggleActive={setCtaActive}
      >
        <Field label="Heading">
          <TextInput
            value={ctaHeading}
            onChange={(e) => setCtaHeading(e.target.value)}
          />
        </Field>
        <Field label="Description">
          <TextArea
            value={ctaDescription}
            onChange={(e) => setCtaDescription(e.target.value)}
          />
        </Field>
        <CTAGroup
          label="Primary CTA"
          text={ctaPrimary.text}
          url={ctaPrimary.url}
          newTab={ctaPrimary.newTab}
          onTextChange={(v) => updateBtn(setCtaPrimary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaPrimary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaPrimary, "newTab", v)}
        />
        <CTAGroup
          label="Secondary CTA"
          text={ctaSecondary.text}
          url={ctaSecondary.url}
          newTab={ctaSecondary.newTab}
          onTextChange={(v) => updateBtn(setCtaSecondary, "text", v)}
          onUrlChange={(v) => updateBtn(setCtaSecondary, "url", v)}
          onNewTabChange={(v) => updateBtn(setCtaSecondary, "newTab", v)}
        />
        <Field label="Contact Line">
          <TextInput
            value={ctaContactLine}
            onChange={(e) => setCtaContactLine(e.target.value)}
          />
        </Field>
      </CollapsibleSection>
    </>
  );
}
